import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { ModuleStatus } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { usePriceCategoriesQuery } from "Modules/Api/Queries/usePriceCategoriesQuery";
import { BackButton } from "Shared/Buttons/BackButton";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { useUpdateModuleMutation } from "../Api/Mutations/useUpdateModuleMutation";
import { useUpdateModuleStatusMutation } from "../Api/Mutations/useUpdateModuleStatusMutation";
import { useModuleDetailQuery } from "../Api/Queries/useModuleDetailQuery";
import { useModulePartnersQuery } from "../Api/Queries/useModulePartnersQuery";
import { EditModuleModal } from "../Components/EditModuleModal";
import { EditModulePartnersModal } from "../Components/EditModulePartnersModal";
import { ModuleAvailablePartners } from "../Components/ModuleAvailablePartners";
import { ModuleInformation } from "../Components/ModuleInformation";
import { ModuleQuestionsSection } from "../Components/ModuleQuestionsSection";
import type { ModuleFormData } from "../Forms/moduleFormSchema";

export const ModuleDetailPage = (): React.ReactNode => {
  const { t } = useResource();

  const { moduleId } = useParams<{ moduleId: string }>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditPartnersModalOpen, setIsEditPartnersModalOpen] = useState(false);

  const { data: module, isLoading } = useModuleDetailQuery(moduleId);
  const { data: priceCategoriesData, isLoading: isPriceCategoriesLoading } =
    usePriceCategoriesQuery();
  const { data: modulePartnersData } = useModulePartnersQuery(moduleId);
  const updateModuleMutation = useUpdateModuleMutation();
  const updateStatusMutation = useUpdateModuleStatusMutation();

  const priceCategoryName = useMemo(() => {
    if (!module || !priceCategoriesData?.items) return undefined;
    const category = priceCategoriesData.items.find(
      (pc) => pc.priceCategoryID === module.priceCategoryID,
    );
    return category?.name;
  }, [module, priceCategoriesData]);

  const enabledPartners = useMemo(
    () => (modulePartnersData?.items ?? []).filter((p) => p.isEnabledForModule),
    [modulePartnersData],
  );

  const handleUpdateModule = async (moduleData: ModuleFormData) => {
    if (!moduleId) return;

    try {
      await updateModuleMutation.mutateAsync({
        moduleID: moduleId,
        data: {
          name: moduleData.name,
          type: moduleData.type,
          priceCategoryID: moduleData.priceCategoryID,
          customPrompt: moduleData.customPrompt,
          customPromptPlainText: moduleData.customPromptPlainText,
          textLlms: moduleData.textLlms,
          voiceLlms: moduleData.voiceLlms,
        },
      });

      toast.success(t(Resources.Modules.EditModuleModal.SuccessMessage));
      setIsEditModalOpen(false);
    } catch {
      // field errors shown in form, non-field errors as toast via useHandleServerError
    }
  };

  const handleStatusChange = async () => {
    if (!moduleId || !module) return;

    const newStatus =
      module.status === ModuleStatus.Active
        ? ModuleStatus.Inactive
        : ModuleStatus.Active;

    try {
      await updateStatusMutation.mutateAsync({
        moduleID: moduleId,
        status: newStatus,
      });

      toast.success(
        newStatus === ModuleStatus.Active
          ? t(Resources.Modules.ModuleDetail.ActivateSuccess)
          : t(Resources.Modules.ModuleDetail.DeactivateSuccess),
      );
    } catch {
      toast.error(t(Resources.Modules.ModuleDetail.StatusChangeError));
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Modules)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!module) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Modules)}>
        <Typography>{t(Resources.Modules.ModuleDetail.NotFound)}</Typography>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout
      preTitle={
        <BackButton
          defaultText={t(Resources.Common.BackToModules)}
          defaultPath={AppRouting.Modules.path()}
        />
      }
      title={module.name}
      action={
        <Button
          variant="contained"
          color={module.status === ModuleStatus.Active ? "error" : "success"}
          onClick={handleStatusChange}
          disabled={updateStatusMutation.isPending}
        >
          {module.status === ModuleStatus.Active
            ? t(Resources.Modules.ModuleDetail.Deactivate)
            : t(Resources.Modules.ModuleDetail.Activate)}
        </Button>
      }
    >
      <Grid container columnSpacing={Spacing.L} rowSpacing={Spacing.M}>
        <Grid size={6}>
          <ModuleInformation
            module={module}
            priceCategoryName={priceCategoryName}
            priceCategoryID={module.priceCategoryID}
            onEdit={() => setIsEditModalOpen(true)}
          />
        </Grid>
        <Grid size={6}>
          <ModuleAvailablePartners
            partners={enabledPartners}
            onEdit={() => setIsEditPartnersModalOpen(true)}
          />
          <Box mt={Spacing.M}>
            <ModuleQuestionsSection moduleID={moduleId} />
          </Box>
        </Grid>
      </Grid>

      <EditModuleModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onConfirm={handleUpdateModule}
        module={module}
        priceCategories={priceCategoriesData?.items ?? []}
        isPriceCategoriesLoading={isPriceCategoriesLoading}
        serverError={updateModuleMutation.error}
      />
      <EditModulePartnersModal
        isOpen={isEditPartnersModalOpen}
        onClose={() => setIsEditPartnersModalOpen(false)}
        moduleID={moduleId}
      />
    </AuthenticatedLayout>
  );
};
